import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { 
  Sun, Cloud, CloudRain, Snowflake, Wind, Droplets, ThermometerSun,
  MapPin, Clock, Euro, Users, Heart, Filter, X, ChevronRight,
  Compass, Waves, Mountain, Utensils, ShoppingBag, Sparkles, Calendar
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Weather icons mapping
const getWeatherIcon = (condition) => {
  switch (condition) {
    case 'sunny': return <Sun className="w-5 h-5 text-yellow-500" />;
    case 'cloudy': return <Cloud className="w-5 h-5 text-gray-500" />;
    case 'rainy': return <CloudRain className="w-5 h-5 text-blue-500" />;
    case 'cold': return <Snowflake className="w-5 h-5 text-cyan-500" />;
    default: return <Sun className="w-5 h-5 text-yellow-500" />;
  }
};

// Category icons mapping
const getCategoryIcon = (category) => {
  switch (category) {
    case 'spiaggia': return <Waves className="w-5 h-5" />;
    case 'cultura': return <Compass className="w-5 h-5" />;
    case 'natura': return <Mountain className="w-5 h-5" />;
    case 'gastronomia': return <Utensils className="w-5 h-5" />;
    case 'relax': return <Sparkles className="w-5 h-5" />;
    case 'shopping': return <ShoppingBag className="w-5 h-5" />;
    default: return <Compass className="w-5 h-5" />;
  }
};

// Category label mapping
const getCategoryLabel = (category) => {
  const labels = {
    'spiaggia': 'Spiaggia',
    'cultura': 'Cultura',
    'natura': 'Natura',
    'gastronomia': 'Gastronomia',
    'relax': 'Relax',
    'shopping': 'Shopping'
  };
  return labels[category] || category;
};

// Weather condition label
const getWeatherLabel = (condition) => {
  const labels = {
    'sunny': 'Soleggiato',
    'cloudy': 'Nuvoloso',
    'rainy': 'Pioggia',
    'cold': 'Freddo'
  };
  return labels[condition] || condition;
};

// Filter categories
const FILTER_CATEGORIES = [
  { value: 'all', label: 'Tutti', icon: null },
  { value: 'spiaggia', label: 'Mare', icon: Waves },
  { value: 'cultura', label: 'Cultura', icon: Compass },
  { value: 'natura', label: 'Natura', icon: Mountain },
  { value: 'gastronomia', label: 'Gastronomia', icon: Utensils },
  { value: 'relax', label: 'Relax', icon: Sparkles },
  { value: 'shopping', label: 'Shopping', icon: ShoppingBag },
];

// Duration filters
const FILTER_DURATIONS = [
  { value: 'all', label: 'Tutte le durate' },
  { value: 'short', label: '1-2 ore' },
  { value: 'half', label: 'Mezza giornata' },
  { value: 'full', label: 'Giornata intera' },
];

// Skeleton loader for cards
const ItineraryCardSkeleton = () => (
  <div className="bg-white rounded-xl shadow-soft overflow-hidden animate-pulse">
    <div className="h-48 bg-gray-200" />
    <div className="p-5">
      <div className="h-4 bg-gray-200 rounded w-1/4 mb-3" />
      <div className="h-6 bg-gray-200 rounded w-3/4 mb-3" />
      <div className="h-4 bg-gray-200 rounded w-full mb-2" />
      <div className="h-4 bg-gray-200 rounded w-2/3 mb-4" />
      <div className="flex gap-4">
        <div className="h-4 bg-gray-200 rounded w-24" />
        <div className="h-4 bg-gray-200 rounded w-20" />
      </div>
    </div>
  </div>
);

// Itinerary Card Component
const ItineraryCard = ({ itinerary, weather, isFavorite, onToggleFavorite }) => {
  const isRecommended = weather?.condizione === itinerary.condizione_meteo;
  
  // Default/fallback image
  const imageUrl = itinerary.immagine_url || 
    `https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=600&h=400&fit=crop`;
  
  return (
    <div className="bg-white rounded-xl shadow-soft overflow-hidden hover:shadow-lg transition-all duration-300 group">
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <img 
          src={imageUrl}
          alt={itinerary.nome}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
          onError={(e) => {
            e.target.src = 'https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=600&h=400&fit=crop';
          }}
        />
        
        {/* Recommended badge */}
        {isRecommended && (
          <div className="absolute top-3 left-3 bg-[#C5A059] text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
            <Sparkles className="w-3 h-3" />
            Consigliato oggi
          </div>
        )}
        
        {/* Favorite button */}
        <button 
          onClick={(e) => {
            e.preventDefault();
            onToggleFavorite(itinerary.id);
          }}
          className="absolute top-3 right-3 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-md hover:bg-white transition-colors"
        >
          <Heart className={`w-5 h-5 ${isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-400'}`} />
        </button>
        
        {/* Category badge */}
        <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 text-[#1A202C]">
          {getCategoryIcon(itinerary.categoria)}
          {getCategoryLabel(itinerary.categoria)}
        </div>
      </div>
      
      {/* Content */}
      <div className="p-5">
        <h3 className="font-cinzel text-lg text-[#1A202C] mb-2 line-clamp-1">
          {itinerary.nome}
        </h3>
        
        <p className="font-manrope text-sm text-[#4A5568] mb-4 line-clamp-2">
          {itinerary.descrizione}
        </p>
        
        {/* Meta info */}
        <div className="flex flex-wrap gap-3 mb-4 text-xs text-[#4A5568]">
          <div className="flex items-center gap-1">
            <MapPin className="w-4 h-4 text-[#C5A059]" />
            <span>{itinerary.luogo || 'Paestum'}</span>
          </div>
          
          {itinerary.durata && (
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4 text-[#C5A059]" />
              <span>{itinerary.durata}</span>
            </div>
          )}
          
          {itinerary.distanza_km && (
            <div className="flex items-center gap-1">
              <Compass className="w-4 h-4 text-[#C5A059]" />
              <span>{itinerary.distanza_km} km</span>
            </div>
          )}
          
          {itinerary.costo_indicativo && (
            <div className="flex items-center gap-1">
              <Euro className="w-4 h-4 text-[#C5A059]" />
              <span>{itinerary.costo_indicativo}</span>
            </div>
          )}
        </div>
        
        {/* Weather compatibility */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 text-xs text-[#4A5568]">
            {getWeatherIcon(itinerary.condizione_meteo)}
            <span>Ideale con {getWeatherLabel(itinerary.condizione_meteo)}</span>
          </div>
          
          <Link 
            to={`/itineraries/${itinerary.id}`}
            className="text-[#C5A059] hover:text-[#B08D45] text-sm font-medium flex items-center gap-1"
          >
            Scopri <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default function ItinerariesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [itineraries, setItineraries] = useState([]);
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [favorites, setFavorites] = useState([]);
  
  // Filters
  const [categoryFilter, setCategoryFilter] = useState(searchParams.get('categoria') || 'all');
  const [durationFilter, setDurationFilter] = useState('all');
  const [showOnlyRecommended, setShowOnlyRecommended] = useState(false);
  
  // Load favorites from localStorage
  useEffect(() => {
    const savedFavorites = localStorage.getItem('itinerary_favorites');
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
  }, []);
  
  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch suggested itineraries (includes weather)
        const suggestedRes = await fetch(`${BACKEND_URL}/api/itineraries/suggested`);
        if (suggestedRes.ok) {
          const data = await suggestedRes.json();
          setWeather(data.meteo);
        }
        
        // Fetch all itineraries
        const itinerariesRes = await fetch(`${BACKEND_URL}/api/itineraries`);
        if (itinerariesRes.ok) {
          const data = await itinerariesRes.json();
          // Filter only active
          setItineraries(data.filter(it => it.attivo !== false));
        } else {
          throw new Error('Errore nel caricamento degli itinerari');
        }
      } catch (err) {
        console.error('Error fetching itineraries:', err);
        setError('Impossibile caricare gli itinerari. Riprova più tardi.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Filter itineraries
  const filteredItineraries = itineraries.filter(it => {
    // Category filter
    if (categoryFilter !== 'all' && it.categoria !== categoryFilter) {
      return false;
    }
    
    // Duration filter
    if (durationFilter !== 'all') {
      const duration = (it.durata || '').toLowerCase();
      if (durationFilter === 'short' && !duration.includes('ora') && !duration.includes('1-2')) {
        return false;
      }
      if (durationFilter === 'half' && !duration.includes('mezza')) {
        return false;
      }
      if (durationFilter === 'full' && !duration.includes('giornata intera')) {
        return false;
      }
    }
    
    // Recommended filter
    if (showOnlyRecommended && weather) {
      if (it.condizione_meteo !== weather.condizione) {
        return false;
      }
    }
    
    return true;
  });
  
  // Toggle favorite
  const toggleFavorite = (id) => {
    setFavorites(prev => {
      const newFavorites = prev.includes(id) 
        ? prev.filter(f => f !== id)
        : [...prev, id];
      localStorage.setItem('itinerary_favorites', JSON.stringify(newFavorites));
      return newFavorites;
    });
  };
  
  // Clear filters
  const clearFilters = () => {
    setCategoryFilter('all');
    setDurationFilter('all');
    setShowOnlyRecommended(false);
    setSearchParams({});
  };
  
  const hasActiveFilters = categoryFilter !== 'all' || durationFilter !== 'all' || showOnlyRecommended;
  
  return (
    <Layout>
      <div className="min-h-screen bg-[#F9F9F7]">
        {/* Hero Section with Weather */}
        <section className="relative bg-gradient-to-br from-[#1A202C] to-[#2D3748] text-white py-16 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              {/* Title */}
              <div>
                <p className="font-cormorant text-[#C5A059] tracking-[0.2em] uppercase text-sm mb-2">
                  Scopri il territorio
                </p>
                <h1 className="font-cinzel text-4xl md:text-5xl mb-4">
                  Cosa Fare Oggi
                </h1>
                <p className="font-manrope text-gray-300 max-w-xl">
                  Itinerari e esperienze selezionate in base al meteo e alla stagione. 
                  Lasciati ispirare dalla bellezza del Cilento.
                </p>
              </div>
              
              {/* Weather Card */}
              {weather && (
                <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 min-w-[280px]">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 bg-white/20 rounded-full">
                      {weather.condizione === 'sunny' && <Sun className="w-8 h-8 text-yellow-400" />}
                      {weather.condizione === 'cloudy' && <Cloud className="w-8 h-8 text-gray-300" />}
                      {weather.condizione === 'rainy' && <CloudRain className="w-8 h-8 text-blue-300" />}
                      {weather.condizione === 'cold' && <Snowflake className="w-8 h-8 text-cyan-300" />}
                    </div>
                    <div>
                      <p className="text-3xl font-bold">{weather.temperatura}°C</p>
                      <p className="text-gray-300 text-sm">{weather.descrizione}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2 text-xs text-gray-300">
                    <div className="flex items-center gap-1">
                      <Droplets className="w-4 h-4" />
                      <span>{weather.umidita}%</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Wind className="w-4 h-4" />
                      <span>{weather.vento} km/h</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      <span>Paestum</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
        
        {/* Filters Section */}
        <section className="bg-white border-b sticky top-0 z-10 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex flex-wrap items-center gap-3">
              {/* Category filters */}
              <div className="flex flex-wrap gap-2">
                {FILTER_CATEGORIES.map(cat => (
                  <button
                    key={cat.value}
                    onClick={() => {
                      setCategoryFilter(cat.value);
                      if (cat.value !== 'all') {
                        setSearchParams({ categoria: cat.value });
                      } else {
                        setSearchParams({});
                      }
                    }}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                      categoryFilter === cat.value
                        ? 'bg-[#C5A059] text-white'
                        : 'bg-gray-100 text-[#4A5568] hover:bg-gray-200'
                    }`}
                  >
                    {cat.icon && <cat.icon className="w-4 h-4" />}
                    {cat.label}
                  </button>
                ))}
              </div>
              
              {/* Weather recommended toggle */}
              {weather && (
                <button
                  onClick={() => setShowOnlyRecommended(!showOnlyRecommended)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                    showOnlyRecommended
                      ? 'bg-[#C5A059] text-white'
                      : 'bg-gray-100 text-[#4A5568] hover:bg-gray-200'
                  }`}
                >
                  <Sparkles className="w-4 h-4" />
                  Consigliati oggi
                </button>
              )}
              
              {/* Clear filters */}
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 rounded-full text-sm font-medium text-red-600 hover:bg-red-50 flex items-center gap-1"
                >
                  <X className="w-4 h-4" />
                  Rimuovi filtri
                </button>
              )}
            </div>
          </div>
        </section>
        
        {/* Itineraries Grid */}
        <section className="py-10 px-4">
          <div className="max-w-7xl mx-auto">
            {loading ? (
              // Skeleton loaders
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <ItineraryCardSkeleton key={i} />
                ))}
              </div>
            ) : error ? (
              // Error state
              <div className="text-center py-16">
                <div className="text-6xl mb-4">😕</div>
                <h3 className="font-cinzel text-xl text-[#1A202C] mb-2">{error}</h3>
                <Button 
                  onClick={() => window.location.reload()}
                  className="mt-4 bg-[#C5A059] hover:bg-[#B08D45]"
                >
                  Riprova
                </Button>
              </div>
            ) : filteredItineraries.length === 0 ? (
              // No results
              <div className="text-center py-16">
                <div className="text-6xl mb-4">🔍</div>
                <h3 className="font-cinzel text-xl text-[#1A202C] mb-2">
                  Nessun itinerario trovato
                </h3>
                <p className="font-manrope text-[#4A5568] mb-4">
                  Prova a modificare i filtri per vedere più risultati.
                </p>
                <Button 
                  onClick={clearFilters}
                  variant="outline"
                  className="border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059] hover:text-white"
                >
                  Rimuovi filtri
                </Button>
              </div>
            ) : (
              // Itineraries grid
              <>
                <p className="text-sm text-[#4A5568] mb-6">
                  {filteredItineraries.length} itinerar{filteredItineraries.length === 1 ? 'io' : 'i'} disponibil{filteredItineraries.length === 1 ? 'e' : 'i'}
                </p>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredItineraries.map(itinerary => (
                    <ItineraryCard
                      key={itinerary.id}
                      itinerary={itinerary}
                      weather={weather}
                      isFavorite={favorites.includes(itinerary.id)}
                      onToggleFavorite={toggleFavorite}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </section>
        
        {/* Weather-based suggestion */}
        {weather && !loading && filteredItineraries.length > 0 && (
          <section className="py-10 px-4 bg-gradient-to-br from-[#C5A059]/10 to-[#C5A059]/5">
            <div className="max-w-4xl mx-auto text-center">
              <h3 className="font-cinzel text-2xl text-[#1A202C] mb-4">
                Il nostro consiglio per oggi
              </h3>
              <p className="font-manrope text-[#4A5568] mb-6">
                {weather.temperatura > 33 
                  ? "Con questo caldo, ti consigliamo attività al mare la mattina presto e musei o luoghi freschi nel pomeriggio."
                  : weather.condizione === 'rainy'
                  ? "Giornata perfetta per scoprire musei, grotte o concedersi una cooking class!"
                  : weather.condizione === 'sunny'
                  ? "Una giornata perfetta per esplorare le spiagge e i templi di Paestum!"
                  : "Giornata ideale per passeggiate culturali e degustazioni."
                }
              </p>
            </div>
          </section>
        )}
      </div>
    </Layout>
  );
}
